import { useCallback, useEffect, useState } from "react";

/**
 * What the product looks like on this screen.
 * ---------------------------------------------------------------------------
 * Three things the page was drawn at one size and never asked about: how big
 * the tab strip is, how big the page header's icon buttons are, and how tall
 * the header band itself is. A screen that is 1280 wide and one that is 3400
 * wide do not want the same answer, so each is a number someone can set rather
 * than a constant somebody once chose.
 *
 * Each is a SCALE, not a size: it multiplies every measure the thing is built
 * from, so the proportions hold wherever it lands. The two the stylesheets own
 * are published as custom properties on the document - `--ph-icon-k` and
 * `--ph-h-k` - and the strip's own is read by the component that draws it.
 *
 * Kept on this machine, like the renderer and the palettes: this is about the
 * screen in front of someone, not about the estate.
 * ---------------------------------------------------------------------------
 */

const KEYS = {
  menu: "ui.hmenu.scale",
  icon: "ui.header.icon",
  height: "ui.header.height",
  /* The card fill had no name here, so every read and write of it went to a
     key called "undefined" - it worked, in the way that putting the milk in
     the oven works until somebody else looks for it. A value saved under the
     old non-name is not carried over: it goes back to the default once, and
     is kept properly from then on. */
  alphaFrom: "ui.card.alpha.from",
  alphaTo: "ui.card.alpha.to",
  solid: "ui.card.solid",
  depth: "ui.card.depth",
  lights: "ui.cards.rendered",
  zoom: "ui.cards.maxzoom",
  glass: "ui.cards.glass",
};

export const APPEARANCE = {
  /* A quarter smaller than the strip was drawn. */
  menu: { def: 0.75, min: 0.5, max: 1.25, step: 0.05, label: "Menu size" },
  icon: { def: 1, min: 0.7, max: 1.5, step: 0.05, label: "Header icons" },
  height: { def: 1, min: 0.7, max: 1.4, step: 0.05, label: "Header height" },
  /* How solid a card's tint is - the two ends of it.
     -----------------------------------------------------------------------
     Not one value for every card: a card's surface is seeded a little lighter
     or darker than its neighbours, which is what stops a hundred of them
     reading as one sheet. What was set here used to be the TOP of that range
     with the spread hanging below it at a fixed width, which meant the one
     thing somebody might actually want to change - how far apart the cards
     are - could not be changed at all.

     So it is the range itself: the palest a card may be and the most solid,
     and every card takes a place between them from its own id. Put them
     together and every card is the same weight; pull them apart and the shelf
     gets its depth back. Either order is allowed - dragging the bottom past
     the top swaps them rather than refusing. */
  alphaFrom: { def: 0.75, min: 0.2, max: 1, step: 0.01, label: "Card fill from" },
  alphaTo: { def: 0.98, min: 0.2, max: 1, step: 0.01, label: "Card fill to" },

  /* ...or no transparency at all.
     -----------------------------------------------------------------------
     A weight is a card painted at less than full strength, so the page shows
     through it - and so does whatever else is behind it, which on a sphere is
     thirty other cards. That is the right answer for a shelf and the wrong
     one for a ball.

     With this on, the weight is still worked out for every card and then
     SPENT: the tint is mixed with the page's own ground in that proportion,
     and the result is painted solid. The same colour to the eye, no
     transparency anywhere - nothing shows through a card, and on the GPU the
     cards stop needing to be drawn back to front. */
  solid: { def: 0, min: 0, max: 1, step: 1, label: "Solid cards", as: "switch" },

  /* How thick a card is.
     -----------------------------------------------------------------------
     A card used to be a quad - no thickness at all, which is right for a
     shelf seen square on and wrong the moment the scene is turned: a sphere
     of them showed a hundred cards edge-on as a hundred invisible lines. With
     a depth they are objects, their edges catch the light, and the
     arrangement reads as a thing made of cards rather than as a picture of
     one. Nothing but the GPU renderer can do it: a DOM card has one face.

     Zero is still allowed, and is still a quad - cheaper, and the flattest
     possible shelf for anyone who wants that. */
  depth: { def: 18, min: 0, max: 80, step: 1, label: "Card depth", as: "px" },

  /* ...and whether the scene is lit at all.
     -----------------------------------------------------------------------
     Off, the cards are their own colour and nothing else touches them, which
     is the fastest thing a GPU can draw and what the page has always looked
     like. On, there are lights in the scene, the materials answer to them,
     and the frame goes through a bloom and a film curve on its way out - the
     depth above starts to read as depth, the edges catch a highlight, and the
     accent lifts off the dark. It costs a good deal more per frame, which is
     why it is a choice and not the default. */
  lights: { def: 0, min: 0, max: 1, step: 1, label: "Rendered", as: "switch" },

  /* How far INTO the stage the wheel will go, on every page that stands its
     cards on one. A multiplier rather than a fraction - at 12 a card fills a
     good part of the screen, at 3 the shelf stays a shelf - so it is written
     as "12x" rather than as a percentage of something. It caps reading a card
     up close as well, since that is the same magnification by another name. */
  zoom: { def: 12, min: 3, max: 24, step: 1, label: "Max zoom", as: "x" },

  /* ...and what a card is made of.
     -----------------------------------------------------------------------
     Off, a card is a painted surface: a tint at a weight, which is what every
     setting above is about. On, it is a pane of glass - the ground goes on
     showing through it, the writing floats, and what tells you where the card
     ends is its edge, lit along the top the way a real pane is. The panel
     beside the stage goes with it, so what is being read sits over the scene
     rather than beside it.

     It takes the place of "Solid cards" rather than arguing with it: glass
     IS transparency, so a card cannot be both. The weights above still do
     their work - a card's own place in the range is how clear its pane is. */
  glass: { def: 0, min: 0, max: 1, step: 1, label: "Glass cards", as: "switch" },
};

/* One event, so every page open at once moves while the slider is moving -
   `storage` only fires in the OTHER tabs. */
const CHANGED = "appearance-changed";

/* Kept for the strip, which asks for its own number by name. */
export const HMENU_SCALE_DEFAULT = APPEARANCE.menu.def;
export const HMENU_SCALE_MIN = APPEARANCE.menu.min;
export const HMENU_SCALE_MAX = APPEARANCE.menu.max;

function clamp(which, value) {
  const { def, min, max } = APPEARANCE[which];
  /* `Number(value) || def` was the obvious way to write this and it threw
     away every ZERO: a switch turned off asked to be saved as 0, 0 is falsy,
     and what went to storage was the default - so any switch whose default is
     on could not be turned off at all. The ones defaulting to off hid it. */
  const n = Number(value);
  const asked = Math.round((Number.isFinite(n) ? n : def) * 100) / 100;
  return Math.min(max, Math.max(min, asked));
}

export function loadAppearance(which) {
  try {
    const raw = parseFloat(localStorage.getItem(KEYS[which]));
    if (!Number.isFinite(raw)) return APPEARANCE[which].def;
    return clamp(which, raw);
  } catch (e) { return APPEARANCE[which].def; }
}

export function loadAllAppearance() {
  return { menu: loadAppearance("menu"), icon: loadAppearance("icon"), height: loadAppearance("height") };
}

/**
 * The two the stylesheets read, published on the document.
 *
 * Written straight onto the root rather than handed down through React: the
 * header is drawn by half a dozen components and by page-header.css, and a
 * variable on the document is the one place all of them already look.
 */
export function applyAppearance() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--ph-icon-k", String(loadAppearance("icon")));
  root.style.setProperty("--ph-h-k", String(loadAppearance("height")));
  /* Stamped on the document rather than handed down: the cards are drawn by
     three stylesheets and two renderers, and an attribute on the root is the
     one place all of them can see without being told. */
  if (loadAppearance("glass") >= 1) root.setAttribute("data-glass-cards", "");
  else root.removeAttribute("data-glass-cards");
}

export function saveAppearance(which, value) {
  const next = clamp(which, value);
  try { localStorage.setItem(KEYS[which], String(next)); } catch (e) { /* private window */ }
  applyAppearance();
  window.dispatchEvent(new Event(CHANGED));
  return next;
}

export function forgetAppearance(which) {
  try { localStorage.removeItem(KEYS[which]); } catch (e) { /* private window */ }
  applyAppearance();
  window.dispatchEvent(new Event(CHANGED));
  return APPEARANCE[which].def;
}

/** Everything back as it was drawn. */
export function forgetAllAppearance() {
  Object.keys(APPEARANCE).forEach(which => {
    try { localStorage.removeItem(KEYS[which]); } catch (e) { /* private window */ }
  });
  applyAppearance();
  window.dispatchEvent(new Event(CHANGED));
}

/** One of them, kept in step with whatever else is open. */
export function useAppearance(which) {
  const [value, setValue] = useState(() => loadAppearance(which));
  useEffect(() => {
    const sync = () => setValue(loadAppearance(which));
    window.addEventListener(CHANGED, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener("storage", sync);
    };
  }, [which]);
  const set = useCallback((next) => saveAppearance(which, next), [which]);
  return [value, set];
}

/** The strip's own, by the name the component that draws it uses. */
export function useHMenuScale() {
  const [value] = useAppearance("menu");
  return value;
}

export const saveHMenuScale = (v) => saveAppearance("menu", v);
export const forgetHMenuScale = () => forgetAppearance("menu");

/* Set on load, so a page that is opened cold is drawn at the size it was left
   at rather than at the default for a frame. */
applyAppearance();

export default useAppearance;
